import React from "react";
import { GSForm, GSSidePanelPage } from "golfstatus_react_components";
import { faBoxOpen, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useMatch, useNavigate } from "react-router-dom";

//import scss
import "./tournament-package-create-edit.scss";
import { getPackageForm } from "../../../../forms/TournamentPackageForm";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  clearNotifications,
  copyCurrentPackage,
  copyTournamentPackage,
  createTournamentPackage,
  getTournamentPackage,
  saveTournamentPackage,
  selectCurrentPackageRounds,
  selectCurrentTournamentPackage,
  selectMinTechSponsorPackagePrice,
  selectNotifications,
  setCurrentPackage,
} from "../../../../reducers/packageSlice";
import { useSelector } from "react-redux";
import { getNotificationItemInfo } from "../../../../helpers/Converters";
import {
  getSaveBannerActions,
  useNotificationBanner,
  useNotificationNavigation,
} from "../../../../hooks/notificationHooks";
import { useFormValidation } from "../../../../hooks/formHooks";

//Name the component
const TournamentPackageCreateEdit = (props) => {
  //hooks
  const navigate = useNavigate();
  const location = useLocation();
  const match = useMatch("/:tournamentID/promotion/packages/:packageID/*");
  const dispatch = useDispatch();

  //selectors
  const currentPackage = useSelector(selectCurrentTournamentPackage);
  const packageNotifications = useSelector(selectNotifications);
  const packageRounds = useSelector(selectCurrentPackageRounds);
  const packageMinTechSponsorPrice = useSelector(selectMinTechSponsorPackagePrice);

  //context
  const [context, isValid, setIsValid] = useFormValidation(false);

  //custom hooks
  useNotificationNavigation(
    "created",
    packageNotifications,
    `/${match?.params?.tournamentID}/promotion/packages/[id]/package-items?step=2`
  );

  const type = match?.params?.['*'] || "add";
  const isAdd = type === 'add';
  const isCopy = type === 'copy';
  const isEdit = type === 'edit';

  //effects
  useEffect(() => {
    // unless you come straight here via url, currentPackage is already loaded
    if (match?.params?.packageID && currentPackage?.id !== match?.params?.packageID && !isAdd) {
      dispatch(getTournamentPackage(match.params.packageID));
    } else if (isAdd) {
      dispatch(setCurrentPackage({ name: "", cost: "", availableLimit: "" }));
    }
  }, [dispatch, currentPackage?.id, match?.params?.packageID, isAdd]);

  useEffect(() => {
    // if they are copying, we want to default name to `name + (copy)` and state to 'active'
    if (isCopy && currentPackage?.id && !currentPackage?.ogName && currentPackage?.id === match?.params?.packageID) {
      dispatch(copyCurrentPackage());
    }
  }, [dispatch, currentPackage?.id, currentPackage?.ogName, match?.params?.packageID, isCopy]);

  context.validationFailed = () => {
    if (isValid) {
      setIsValid(false);
    }
  };

  context.isCopy = isCopy;
  context.minTechSponsorPrice = packageMinTechSponsorPrice;

  // API sends hasTechPackageItem attribute with the tournament package data
  const isTechPackage = () => {
    return !!currentPackage?.hasTechPackageItem;
  }

  context.isValidPackagePrice = () => {
    return !isTechPackage() || currentPackage.cost >= packageMinTechSponsorPrice
  }

  context.isValidPackageInventory = () => {
    // no need to do extra processing, if it's < 0, fail
    if (currentPackage.availableLimit < 0) return false;

    return !isTechPackage() || currentPackage.availableLimit <= 1;
  }

  context.updatePackage = (value, property) => {
    setIsValid(true);
    let update = { ...currentPackage };
    update[property] = value;
    dispatch(setCurrentPackage(update));
  };

  const save = () => {
    setUnsaved(false)
    setIsValid(false);
    let update = { ...currentPackage };
    update.tournamentKey = match?.params?.tournamentID;
    if (isAdd) {
      dispatch(createTournamentPackage(update));
    } else if (isCopy) {
      dispatch(copyTournamentPackage(update)).then((res) => {
        const newId = res?.payload?.id;
        if (newId) {
          navigate(`/${match?.params?.tournamentID}/promotion/packages/${newId}`);
        }
      });
    } else {
      update.tournamentRounds = packageRounds;
      dispatch(saveTournamentPackage(update));
    }
  };

  const getDrawerActions = () => {
    let actions = [
      {
        name: isEdit ? "Save" : "Save & Continue",
        action: save,
        isDisabled: !isValid,
        type: "black",
      },
      {
        name: isCopy ? "discard" : "cancel",
        action: leftNavigation,
        type: "grey"
      },
    ];
    return actions;
  };

  const leftNavigation = () => {
    if(isValid && bannerNotifications?.type !== "unsaved") {
      setUnsaved(true);
      return;
    }

    timeoutAction();

    if (location.key === "default") {
      navigate(`/${match?.params?.tournamentID}/promotion/packages/${match?.params?.packageID}`);
    } else {
      navigate(-1);
    }
  };

  const getNavigation = () => {
    //Add Title to your component
    return {
      title: `Package Details`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const getFormTitle = () => {
    const title = type[0].toUpperCase() + type.substring(1);

    return {
      formTitle: `${title} Package`,
      formActions: [
        {
          actionType: "toggle",
          pageActionProps: {
            value: currentPackage.state !== "inactive",
            trueDescription: "Active",
            falseDescription: "Inactive",
            rowReverse: true,
            onClick: () =>
              context.updatePackage(
                currentPackage.state === "inactive" ? "active" : "inactive",
                "state"
              ),
          },
        },
      ],
    };
  };

  const getContent = () => {
    return (
      <GSForm
        {...getFormTitle()}
        formSections={getPackageForm(context, currentPackage)}
      ></GSForm>
    );
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  const getDrawer = () => {
    return {
      actions: getDrawerActions(),
    };
  };

  //banner custom hooks
  let notificationSettings = {
    notifications: packageNotifications,
    saveAction: save,
    bannerActions: getSaveBannerActions(save, leftNavigation),
    timeoutAction,
    defaultBanner:
      isAdd
        ? {
            title: getNotificationItemInfo(
              `Start by adding basic package details.`,
              faBoxOpen,
              "Step 1 of 2"
            ),
            state: "grey",
          }
        : false,
  };

  const [bannerNotifications, setUnsaved] = useNotificationBanner(
    notificationSettings
  );

  return (
    //name the component tag
    <tournament-package-create-edit>
      <GSSidePanelPage
        header={getNavigation()}
        banner={bannerNotifications}
        content={getContent()}
        drawer={getDrawer()}
      ></GSSidePanelPage>
    </tournament-package-create-edit>
  );
};

//export the new namet
export default TournamentPackageCreateEdit;
